// Lightweight WordPress REST API client used by Materiaal Maatjes components
// Centralizes base URL handling, headers, credentials, error handling and common endpoints.

export default class WpApiClient {
  constructor(options = {}) {
    const globalBaseRaw = (window?.MateriaalMaatjes?.apiBase || '').trim().replace(/\/$/, '')
    const fromOptionsRaw = (options.base || '').trim().replace(/\/$/, '')

    // Normalize provided base to always include WP REST namespace version when starting with /wp-json
    const normalizeBase = (b) => {
      if (!b) return ''
      if (/^https?:\/\//i.test(b)) return b // absolute URL left as-is
      if (b.startsWith('/wp-json')) {
        // If no "/wp/v{n}" segment is present, append default "/wp/v2"
        if (!/\/wp\/v\d+(\/|$)/.test(b)) return `${b}/wp/v2`
      }
      return b
    }

    const globalBase = normalizeBase(globalBaseRaw)
    const fromOptions = normalizeBase(fromOptionsRaw)

    this.base = fromOptions || globalBase || '/wp-json/wp/v2'
    this.cacheBust = options.cacheBust ?? true

    this.credentials = options.credentials || 'same-origin'
    this.defaultHeaders = {
      Accept: 'application/json',
      ...(options.headers || {}),
    }
  }

  buildUrl(path, params) {
    const rawPath = String(path || '')
    const isAbs = /^https?:\/\//i.test(rawPath) || rawPath.startsWith('/wp-json')
    const base = isAbs ? '' : this.base.replace(/\/$/, '')
    const p = rawPath.replace(/^\//, '')
    const url = new URL(`${base}/${p}`, window.location.origin)
    if (params && typeof params === 'object') {
      Object.entries(params).forEach(([k, v]) => {
        if (v == null) return
        url.searchParams.set(k, Array.isArray(v) ? v.join(',') : String(v))
      })
    }
    return url.toString()
  }

  async request(path, { method = 'GET', headers = {}, body, params } = {}) {
    const url = this.buildUrl(path, params)
    const res = await fetch(url, {
      method,
      headers: { ...this.defaultHeaders, ...headers },
      credentials: this.credentials,
      body,
    })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      const msg = text || `${res.status} ${res.statusText}`
      const err = new Error(msg)
      err.status = res.status
      throw err
    }
    const contentType = res.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      const data = await res.json()
      return { data, res }
    }
    const data = await res.text()
    return { data, res }
  }

  get(path, params, headers) { 
    if (this.cacheBust) {
      params = { ...params, _t: Date.now() }
    }
    return this.request(path, { method: 'GET', params, headers }) 
  }
  post(path, body, headers) { return this.request(path, { method: 'POST', body, headers }) }

  // --- Admin AJAX helper ---
  getAjaxUrl() {
    const fallback = '/wp-admin/admin-ajax.php'
    try {
      const u = (window?.materiaalAjax?.ajaxurl || fallback)
      // ensure absolute against origin
      return new URL(u, window.location.origin).toString()
    } catch (_) { return fallback }
  }

  async postAjax(action, fields = {}) {
    const url = this.getAjaxUrl()
    const fd = new FormData()
    fd.append('action', action)
    // Attach provided fields
    Object.entries(fields).forEach(([k, v]) => {
      if (v == null) return
      fd.append(k, v)
    })
    // Auto-append nonce if not provided
    if (!fd.has('nonce')) {
      const n = window?.materiaalAjax?.nonce
      if (n) fd.append('nonce', n)
    }
    const res = await fetch(url, { method: 'POST', body: fd, credentials: this.credentials })
    const ct = res.headers.get('content-type') || ''
    if (ct.includes('application/json')) {
      return await res.json()
    }
    // Fallback: probeer tekstrespons te lezen en JSON te parsen indien mogelijk
    const text = await res.text().catch(() => '')
    try {
      const parsed = JSON.parse(text)
      return parsed
    } catch (_) {
      // Standaardiseer foutmelding i.p.v. cryptische "0" of HTML
      console.error('Non-JSON response from server:', text)
      if (text === '0' || text === '-1') {
        const msg = (action === 'materiaal_forgot_password') 
          ? (window?.MateriaalMaatjes?.i18n?.messages?.forgotNotFound || 'Er is geen account bekend met dit e-mailadres. Controleer of het e-mailadres juist is of neem contact op met info@materiaalmaatjes.nl.')
          : 'Sessie verlopen of ongeldige aanvraag. Ververs de pagina.'
        throw new Error(msg)
      }
      const msg = (text && text.trim() && text.trim().length < 140) ? text.trim() : `Onverwachte serverrespons (geen JSON)`
      throw new Error(msg)
    }
  }

  // Domain helpers used by components
  async getCategories({ taxonomy = 'materiaal_categorie', perPage = 100, fields = 'id,name,slug,count,description,parent,acf,image_url' } = {}) {
    const { data } = await this.get(`/${taxonomy}`, { per_page: perPage, _fields: fields })
    return Array.isArray(data) ? data : []
  }

  async getMaterials({ endpoint = '/materiaal', page = 1, perPage = 24, sort = 'newest', search = '', categories = [], location = '', radius, type, location_lat, location_lon } = {}) {
    const params = { page, per_page: perPage, _embed: 1 }
    if (search) params.search = search
    if (categories?.length) params.materiaal_categorie = categories.join(',')
    if (location) params.location = String(location)
    if (radius != null && radius !== '' && !Number.isNaN(Number(radius))) params.radius = Number(radius)
    if (location_lat != null && location_lon != null) { params.location_lat = Number(location_lat); params.location_lon = Number(location_lon) }
    if (type) params.type = String(type)
    switch (sort) {
      case 'price-asc': params.orderby = 'meta_value_num'; params.meta_key = 'mm_waarde_eur'; params.order = 'asc'; break
      case 'price-desc': params.orderby = 'meta_value_num'; params.meta_key = 'mm_waarde_eur'; params.order = 'desc'; break
      case 'oldest': params.orderby = 'date'; params.order = 'asc'; break
      case 'newest': default: params.orderby = 'date'; params.order = 'desc'; break
    }

    // Normalize endpoint to a valid materials collection path
    const normalizeMaterialsEndpoint = (ep) => {
      const d = '/wp-json/wp/v2/materiaal'
      if (!ep) return d
      let e = String(ep).trim()
      // If absolute URL, trust it
      if (/^https?:\/\//i.test(e)) return e
      // Ensure leading slash
      if (!e.startsWith('/')) e = `/${e}`
      // Cases starting with /wp-json
      if (e.startsWith('/wp-json')) {
        // If exactly /wp-json or /wp-json/
        if (/^\/wp-json\/?$/.test(e)) return d
        // If /wp-json/wp/vN optionally ending with slash
        if (/^\/wp-json\/wp\/v\d+\/?$/.test(e)) return `${e.replace(/\/$/, '')}/materiaal`
        return e // already full path like /wp-json/wp/v2/materiaal
      }
      // If starts with /wp/vN, prefix /wp-json
      if (/^\/wp\/v\d+(\/|$)/.test(e)) return `/wp-json${e}`.replace(/\/$/, '')
      // If it's just a resource path like /materiaal
      if (/^\/[a-z0-9_-]+(\/|$)/i.test(e)) return `/wp-json/wp/v2${e}`
      // Fallback to default
      return d
    }

    const finalPath = normalizeMaterialsEndpoint(endpoint)

    const { data, res } = await this.get(finalPath, params)
    const totalPages = Number(res.headers.get('X-WP-TotalPages')) || 1
    return { items: Array.isArray(data) ? data : [], totalPages }
  }

  async getMaterialBySlug(slug) {
    if (!slug) return null
    const { data } = await this.get('/materiaal', { slug, _embed: 1, per_page: 1 })
    return Array.isArray(data) && data.length ? data[0] : null
  }

  async getMediaByParent(parentId, { perPage = 50 } = {}) {
    if (!parentId) return []
    const { data } = await this.get('/media', { parent: parentId, per_page: perPage })
    return Array.isArray(data) ? data : []
  }

  // --- Auth helpers (admin-ajax based) ---
  async login({ username, password, nonce, turnstileToken } = {}) {
    const result = await this.postAjax('materiaal_login', {
      username,
      password,
      nonce,
      ...(turnstileToken ? { cf_turnstile_token: turnstileToken } : {}),
    })
    return result
  }

  async logout({ nonce } = {}) {
    const result = await this.postAjax('materiaal_logout', { nonce })
    return result
  }

    async registerRequest(payload = {}) {
    const allowed = ['role','firstName','lastName','email','orgName','functionName','street','houseNr','postcode','city','website','phone','cf_turnstile_token','nonce','city_location_data']
    const data = {}
    allowed.forEach(k => { if (payload[k] != null) data[k] = payload[k] })
    const result = await this.postAjax('materiaal_register_request', data)
    return result
  }

  async resetPassword({ login, key, newPassword, nonce } = {}) {
    const result = await this.postAjax('materiaal_reset_password', { login, key, newPassword, nonce })
    return result
  }

  async refreshNonce(forceLoggedOut = false) {
    try {
      const res = await this.postAjax('mm_get_nonce', {
        nonce: 'skip',
        force_logged_out: forceLoggedOut ? 'true' : 'false'
      })
      if (res && res.ok && res.nonce && window.materiaalAjax) {
        window.materiaalAjax.nonce = res.nonce
        return res.nonce
      }
    } catch (_) {}
    return null
  }

  async forgotPassword({ email, nonce, turnstileToken } = {}) {
    const result = await this.postAjax('materiaal_forgot_password', {
      email,
      nonce,
      ...(turnstileToken ? { cf_turnstile_token: turnstileToken } : {}),
    })
    return result
  }

  // --- Profile helpers ---
  async getCurrentUser({ nonce } = {}) {
    return await this.postAjax('materiaal_get_current_user', { nonce })
  }

  async updateProfile(payload = {}) {
    const allowed = ['firstName','lastName','email','email2','orgName','functionName','street','houseNr','postcode','city','city_location_data','website','phone','newsletterFrequency','nonce']
    const data = {}
    allowed.forEach(k => { if (payload[k] !== undefined && payload[k] !== null) data[k] = payload[k] })
    return await this.postAjax('materiaal_update_profile', data)
  }

  async uploadCompanyLogo(file, { nonce } = {}) {
    if (!file) throw new Error('Geen bestand opgegeven')
    const url = this.getAjaxUrl()
    const fd = new FormData()
    fd.append('action', 'materiaal_upload_company_logo')
    if (nonce) fd.append('nonce', nonce)
    fd.append('image', file)
    const res = await fetch(url, { method: 'POST', body: fd, credentials: this.credentials })
    const ct = res.headers.get('content-type') || ''
    if (!ct.includes('application/json')) {
      const text = await res.text().catch(() => '')
      throw new Error(text || `HTTP ${res.status}`)
    }
    return await res.json()
  }
  
  async deleteCompanyLogo({ nonce } = {}) {
    return await this.postAjax('materiaal_delete_company_logo', { nonce })
  }

  async deleteAccount({ nonce } = {}) {
    return await this.postAjax('materiaal_delete_account', { nonce })
  }

  async switchRole({ role, nonce } = {}) {
    return await this.postAjax('materiaal_switch_role', { role, nonce })
  }

  // --- Coordinator helpers ---
  async approveUser(userId, { nonce } = {}) {
    if (!userId) throw new Error('Ongeldige gebruiker')
    const params = { userId }
    if (nonce) params.nonce = nonce
    return await this.postAjax('materiaal_approve_user', params)
  }

  async rejectUser(userId, { reason, nonce } = {}) {
    if (!userId) throw new Error('Ongeldige gebruiker')
    const params = { userId }
    if (reason) params.reason = reason
    if (nonce) params.nonce = nonce
    return await this.postAjax('materiaal_reject_user', params)
  }

  async deactivateUser(userId, { reason, nonce } = {}) {
    if (!userId) throw new Error('Ongeldige gebruiker')
    const params = { userId }
    if (reason) params.reason = reason
    if (nonce) params.nonce = nonce
    return await this.postAjax('materiaal_deactivate_user', params)
  }

  async updateUserRegion(userId, { regionId, regionIds, nonce } = {}) {
    if (!userId) throw new Error('Ongeldige gebruiker')
    const params = { userId }
    if (regionIds !== undefined) params.regionIds = regionIds
    else if (regionId !== undefined) params.regionId = regionId
    if (nonce) params.nonce = nonce
    return await this.postAjax('materiaal_update_user_region', params)
  }

  async getAllRegions({ nonce } = {}) {
    const params = {}
    if (nonce) params.nonce = nonce
    return await this.postAjax('materiaal_get_all_regions', params)
  }

  // --- Materials (producten) ---
  async claimMaterial(materialId, { nonce } = {}) {
    if (!materialId) throw new Error('Ongeldig materiaal ID')
    return await this.postAjax('materiaal_claim', { materialId, nonce })
  }

  async listMyClaims({ page = 1, perPage = 20, search = '', sort = 'date_desc', nonce } = {}) {
    // Verwachte backend action: 'materiaal_list_my_claims'
    // Parameters: page, per_page, search, sort
    const params = { page, per_page: perPage, nonce }
    if (search) params.search = search
    if (sort) params.sort = sort
    try {
      const res = await this.postAjax('materiaal_list_my_claims', params)
      // Normaliseer respons
      // Accepteer { ok:true, claims:[], total: N } of { success:true, data:{ items:[], total: N } }
      if (res && (res.ok || res.success)) {
        const claims = res.claims || (res.data && (res.data.claims || res.data.items)) || res.items || []
        const total = Number(res.total || (res.data && res.data.total) || 0)
        return { ok: true, claims: Array.isArray(claims) ? claims : [], total }
      }
      return { ok: false, claims: [], total: 0, error: res && (res.error || res.message) }
    } catch (e) {
      return { ok: false, claims: [], total: 0, error: e?.message || 'Onbekende fout' }
    }
  }

  async listMyClaimsCompany({ page = 1, perPage = 20, search = '', sort = 'date_desc', nonce } = {}) {
    const params = { page, per_page: perPage, nonce }
    if (search) params.search = search
    if (sort) params.sort = sort
    try {
      const res = await this.postAjax('materiaal_list_my_claims_company', params)
      if (res && (res.ok || res.success)) {
        const claims = res.claims || (res.data && (res.data.claims || res.data.items)) || res.items || []
        const total = Number(res.total || (res.data && res.data.total) || 0)
        return { ok: true, claims: Array.isArray(claims) ? claims : [], total }
      }
      return { ok: false, claims: [], total: 0, error: res && (res.error || res.message) }
    } catch (e) {
      return { ok: false, claims: [], total: 0, error: e?.message || 'Onbekende fout' }
    }
  }

  async updateClaim({ materialId, status, nonce } = {}) {
    if (!materialId) throw new Error('Ongeldig materiaal ID')
    const params = { materialId }
    if (status != null) params.status = status
    if (nonce) params.nonce = nonce
    const res = await this.postAjax('materiaal_update_claim', params)
    return res
  }

  async releaseClaim(materialId, { reason, nonce } = {}) {
    if (!materialId) throw new Error('Ongeldig materiaal ID')
    const params = { materialId, nonce }
    if (reason != null) params.reason = reason
    return await this.postAjax('materiaal_release_claim', params)
  }

  async createMaterial({
    title,
    content,
    excerpt,
    categories, // array of term IDs or comma-separated string
    waarde,
    afmetingen,
    gewicht,
    locatie,
    locationData,
    status,
    type,
    imageFile, // Featured image File object
    galleryFiles, // Array of File objects (optional)
    nonce,
  } = {}) {
    const fd = new FormData()
    fd.append('action', 'materiaal_create_materiaal')
    if (nonce) fd.append('nonce', nonce)
    if (title != null) fd.append('title', title)
    if (content != null) fd.append('content', content)
    if (excerpt != null) fd.append('excerpt', excerpt)
    if (Array.isArray(categories)) {
      fd.append('categories', categories.join(','))
    } else if (categories != null) {
      fd.append('categories', String(categories))
    }
    if (waarde != null && waarde !== '') fd.append('mm_waarde_eur', String(waarde))
    if (afmetingen != null) fd.append('mm_afmetingen', afmetingen)
    if (gewicht != null) fd.append('mm_gewicht', gewicht)
    if (locatie != null) fd.append('mm_locatie', locatie)
    if (locationData) fd.append('mm_location_data', JSON.stringify(locationData))
    if (status != null) fd.append('mm_conditie', status)
    if (type != null) fd.append('mm_type', type)
    if (imageFile) fd.append('image', imageFile)
    
    if (Array.isArray(galleryFiles)) {
      galleryFiles.forEach(f => fd.append('gallery[]', f))
    }

    const url = this.getAjaxUrl()
    const res = await fetch(url, { 
      method: 'POST', 
      body: fd, 
      credentials: this.credentials,
      headers: {
        'Accept': 'application/json'
      }
    })
    
    if (!res.ok) {
      let msg = `Server error ${res.status}`
      try {
        const text = await res.text()
        if (text && text.trim()) {
          // Probeer JSON te parsen uit de tekst (soms stuurt WP een error als tekst maar in een 500)
          try {
            const parsed = JSON.parse(text)
            if (parsed.message || parsed.error) {
              msg = parsed.message || parsed.error
            } else {
              msg = text.trim().substring(0, 255)
            }
          } catch (_) {
            if (text.includes('<html') || text.includes('<body')) {
              const match = text.match(/<title>(.*?)<\/title>/i)
              msg = match ? match[1] : `Server fout (${res.status})`
            } else {
              msg = text.trim().substring(0, 255)
            }
          }
        }
      } catch (_) {}
      throw new Error(msg)
    }
    
    const ct = res.headers.get('content-type') || ''
    if (!ct.includes('application/json')) {
      const text = await res.text().catch(() => '')
      // Als de respons "0" is, is de action waarschijnlijk niet geregistreerd
      if (text === '0') throw new Error('Server action niet gevonden (0)')
      throw new Error(text || `Ongeldige serverrespons (geen JSON)`)
    }
    return await res.json()
  }

  async listMyMaterials({ page = 1, perPage = 50, nonce, ...rest } = {}) {
    const params = { page, per_page: perPage, nonce, ...rest }
    const result = await this.postAjax('materiaal_list_my', params)
    return result
  }

  async updateMaterial({
    id,
    title,
    content,
    excerpt,
    categories,
    waarde,
    afmetingen,
    gewicht,
    locatie,
    locationData,
    status,
    type,
    imageFile,
    galleryFiles,
    deleteImage,
    deleteGalleryIds,
    newThumbnailId,
    nonce,
  } = {}) {
    if (!id) throw new Error('Ongeldig materiaal ID')
    const fd = new FormData()
    fd.append('action', 'materiaal_update_materiaal')
    fd.append('id', String(id))
    if (nonce) fd.append('nonce', nonce)
    if (title != null) fd.append('title', title)
    if (content != null) fd.append('content', content)
    if (excerpt != null) fd.append('excerpt', excerpt)
    if (Array.isArray(categories)) fd.append('categories', categories.join(','))
    if (waarde != null && waarde !== '') fd.append('mm_waarde_eur', String(waarde))
    if (afmetingen != null) fd.append('mm_afmetingen', afmetingen)
    if (gewicht != null) fd.append('mm_gewicht', gewicht)
    if (locatie != null) fd.append('mm_locatie', locatie)
    if (locationData) fd.append('mm_location_data', JSON.stringify(locationData))
    if (status != null) fd.append('mm_conditie', status)
    if (type != null) fd.append('mm_type', type)
    if (imageFile) fd.append('image', imageFile)
    if (deleteImage) fd.append('delete_image', '1')
    if (newThumbnailId) fd.append('new_thumbnail_id', String(newThumbnailId))

    if (Array.isArray(galleryFiles)) {
      galleryFiles.forEach(f => fd.append('gallery[]', f))
    }
    if (Array.isArray(deleteGalleryIds)) {
      fd.append('delete_gallery_ids', deleteGalleryIds.join(','))
    }

    const url = this.getAjaxUrl()
    const res = await fetch(url, { 
      method: 'POST', 
      body: fd, 
      credentials: this.credentials,
      headers: {
        'Accept': 'application/json'
      }
    })

    if (!res.ok) {
      let msg = `Server error ${res.status}`
      try {
        const text = await res.text()
        if (text && text.trim()) {
          // Probeer JSON te parsen uit de tekst (soms stuurt WP een error als tekst maar in een 500)
          try {
            const parsed = JSON.parse(text)
            if (parsed.message || parsed.error) {
              msg = parsed.message || parsed.error
            } else {
              msg = text.trim().substring(0, 255)
            }
          } catch (_) {
            if (text.includes('<html') || text.includes('<body')) {
              const match = text.match(/<title>(.*?)<\/title>/i)
              msg = match ? match[1] : `Server fout (${res.status})`
            } else {
              msg = text.trim().substring(0, 255)
            }
          }
        }
      } catch (_) {}
      throw new Error(msg)
    }

    const ct = res.headers.get('content-type') || ''
    if (!ct.includes('application/json')) {
      const text = await res.text().catch(() => '')
      if (text === '0') throw new Error('Server action niet gevonden (0)')
      throw new Error(text || `Ongeldige serverrespons (geen JSON)`)
    }
    return await res.json()
  }

  async deleteMaterial({ id, nonce } = {}) {
    if (!id) throw new Error('Ongeldig materiaal ID')
    const result = await this.postAjax('materiaal_delete_materiaal', { id, nonce })
    return result
  }
}
